/* eslint-disable react-hooks/rules-of-hooks */
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';

import { Select } from '../../../../components/Select';
import { Painel } from '../../../../components/Painel';
import { ButtonsSplits } from '../../../../components/ButtonsSplits';
import { Icon } from '../../../../components/Icon';
import { SearchField } from '../../../../components/SearchField';

import { AddExercisesInTraining } from '../../../../modais/addExercisesInTraining';
import { ChangeTrainingUser } from '../../../../modais/changeTrainingUser';
import { SuggestTraining } from '../../../../modais/suggestTraining';
import { SelectTrainingTemplate } from '../../../../modais/selectTrainingTemplate';

import { api } from '../../../../services/api';
import { colors } from '../../../../styles/tokens';

import {
  AddExerciseButton,
  BackLink,
  Chip,
  ChipsRow,
  Container,
  ContainerAvatar,
  EditorToolbar,
  EstimatedTime,
  ExpirationBanner,
  ExpirationControls,
  ExpirationOverrideInput,
  ExpirationOverrideLabel,
  SaveOverrideButton,
  FiltersRow,
  GhostButton,
  GradientButton,
  Grid,
  LastTraining,
  MainColumn,
  NameRow,
  SectionHeader,
  SectionTitle,
  SegmentedTabs,
  Sidebar,
  SelectWrapper,
  StudentDescription,
  StudentName,
  SummaryLeft,
  SummarySection,
  ToolbarActions,
} from './styles';

import { TrainingCard } from './components/TrainingCard';
import { VolumeAnalysis } from './components/VolumeAnalysis';
import { QuickTools } from './components/QuickTools';
import { PrintWorkoutSheet } from './components/PrintWorkoutSheet';

import noAvatar from '../../../../assets/no-avatar.svg';

import { useAuth } from '../../../../hooks/auth';
import { useFeature } from '../../../../hooks/features';
import { useModal } from '../../../../hooks/modal';
import { useToast } from '../../../../hooks/toast';
import { formatName } from '../../../../utils/formatName';
import { formatSplitLabel } from '../../../../utils/formatSplitLabel';
import { TrainingsService } from '../../../../services/TrainingsService';
import { ExercisesService } from '../../../../services/ExercisesService';
import { IExercise, IMuscleGroup } from '../../../../domains/Exercise';
import { getExercisePrimaryMedia } from '../../../../utils/getExercisePrimaryMedia';
import { ITraining } from '../../../../domains/Training';

interface IUserProps {
  avatar_url: string | undefined;
  fullname: string;
  id: string;
}

interface IExpirationStatus {
  is_expired: boolean;
  is_expiring_soon: boolean;
  expires_at: string;
  expiration_days_override: number | null;
  last_edited_by_name: string | null;
  last_edited_at: string | null;
}

interface IUserExercisesProps {
  variant?: 'full' | 'trainings';
}

const UserExercises: React.FC<IUserExercisesProps> = ({ variant = 'full' }) => {
  const basePath = variant === 'trainings' ? '/trainings' : '/users';

  const formRef = useRef<FormHandles>(null);

  const trainingsService = new TrainingsService();
  const exercisesService = new ExercisesService();

  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<IUserProps>();
  const [lastTraining, setLastTraining] = useState<string>();
  const [lastDayWithTrainings, setLastDayWithTrainings] = useState(0);

  const [trainings, setTrainings] = useState<ITraining[]>([]);
  const [filter, setFilter] = useState<ITraining[]>([]);
  const [notifying, setNotifying] = useState(false);
  const [muscleGroups, setMuscleGroups] = useState<IMuscleGroup[]>([]);
  const [selectValue, setSelectValue] = useState('');
  const [searchText, setSearchText] = useState('');
  const [printSplits, setPrintSplits] = useState<
    { label: string; trainings: ITraining[] }[] | null
  >(null);
  const [preparingPrint, setPreparingPrint] = useState(false);
  const [expiration, setExpiration] = useState<IExpirationStatus | null>(null);
  const [overrideInput, setOverrideInput] = useState('');
  const [savingOverride, setSavingOverride] = useState(false);

  const { user: loggedUser } = useAuth();
  const { addToast } = useToast();
  const { addModal, modals } = useModal();
  const { day, id } = useParams();
  const hasTrainingSuggestionFeature = useFeature('training_suggestion');
  const hasTrainingTemplatesFeature = useFeature('training_templates');

  const getUserData = useCallback(() => {
    api
      .get(`users/gym/${id}`)
      .then(response => {
        setUser(response.data.user);
        if (response.data.lastTraining) {
          setLastTraining(
            format(new Date(response.data.lastTraining), 'dd/MM/yyyy'),
          );
        } else {
          setLastTraining('--/--/----');
        }
      })
      .catch(err => {
        addToast({
          type: 'error',
          title: 'Erro na busca dos dados do usuário.',
          description: err?.response?.status,
        });
      });
  }, [addToast, id]);

  const getTrainings = useCallback(() => {
    setLoading(true);

    trainingsService
      .getTrainingsByUserIdAndDay({ user_id: id, day })
      .then(response => {
        setTrainings(response.data);
        setFilter(response.data);
      })
      .catch(err => {
        addToast({
          type: 'error',
          title: 'Erro na busca dos treinos.',
          description: err?.response?.status,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  }, [addToast, day, trainingsService, id]);

  const getTrainingSplits = useCallback(() => {
    if (id) {
      trainingsService
        .getTrainingSplits(id)
        .then(response => {
          if (response.data.groups.length === 0) {
            setLastDayWithTrainings(0);
            return;
          }

          const last = Math.max.apply(
            0,
            response.data.groups.map(v => v.index),
          );

          setLastDayWithTrainings(last);
        })
        .catch(err => {
          addToast({
            type: 'error',
            title: 'Erro na busca dos treinos da semana.',
            description: err?.response?.status,
          });
        });
    }
  }, [addToast, trainingsService, id]);

  const handleSplitRemoved = useCallback(
    (removedIndex: number) => {
      getTrainingSplits();

      // Se o split apagado é o que está aberto, sai dele -- senão a
      // tela fica presa numa ficha que não existe mais.
      if (Number(day) === removedIndex) {
        navigate(`${basePath}/${id}/${removedIndex - 1}`);
      }
    },
    [basePath, day, getTrainingSplits, id, navigate],
  );

  const getExpiration = useCallback(() => {
    if (!id) return;

    api
      .get(`trainings/gym/${id}/expiration`)
      .then(response => {
        setExpiration(response.data);
        setOverrideInput(
          response.data.expiration_days_override != null
            ? String(response.data.expiration_days_override)
            : '',
        );
      })
      .catch(() => setExpiration(null));
  }, [id]);

  const handleSaveExpirationOverride = useCallback(async () => {
    if (!id) return;

    const parsed = overrideInput.trim() === '' ? null : Number(overrideInput);

    if (parsed !== null && (Number.isNaN(parsed) || parsed <= 0)) {
      addToast({
        type: 'error',
        title: 'Prazo de expiração inválido.',
      });
      return;
    }

    setSavingOverride(true);

    try {
      await api.put(`trainings/gym/${id}/expiration-override`, {
        expiration_days_override: parsed,
      });
      getExpiration();

      addToast({
        type: 'success',
        title: 'Prazo de expiração atualizado.',
      });
    } catch {
      addToast({
        type: 'error',
        title: 'Erro ao atualizar o prazo de expiração.',
        description: 'Tente novamente mais tarde.',
      });
    } finally {
      setSavingOverride(false);
    }
  }, [addToast, getExpiration, id, overrideInput]);

  const getMuscleGroups = useCallback(() => {
    exercisesService
      .getMuscleGroups()
      .then(response => {
        setMuscleGroups([{ id: '', name: 'Todos' }, ...response.data]);
      })
      .catch(() => {
        addToast({
          type: 'error',
          title: 'Erro na busca dos grupos musculares.',
          description:
            'Caso o proplema persista, entre em contato com o administrador do sistema',
        });
      });
  }, [addToast, exercisesService]);

  useEffect(() => {
    const values = trainings.filter(current => {
      if (selectValue) {
        return current.exercise.muscle_groups.some(
          group =>
            group.muscle_group_id === selectValue &&
            current.exercise.name
              .toLowerCase()
              .indexOf(searchText.toLowerCase()) > -1,
        );
      }

      return (
        current.exercise.name.toLowerCase().indexOf(searchText.toLowerCase()) >
        -1
      );
    });

    setFilter(values);
  }, [selectValue, searchText, trainings]);

  const handleChangeSelectForm = useCallback((e: any) => {
    setSelectValue(e.value);
  }, []);

  const handleAddExercise = useCallback(
    async (data: {
      exercise: IExercise;
      interval_min?: number;
      interval_max?: number;
      reps_min?: number;
      reps_max?: number;
      sets_min?: number;
      sets_max?: number;
      additional_information?: string;
    }) => {
      const response = await trainingsService.createTraining({
        user_id: user?.id || '',
        exercise_id: data.exercise.id,
        split_index: Number(day),
        interval_min: data.interval_min,
        interval_max: data.interval_max,
        sets_min: data.sets_min,
        sets_max: data.sets_max,
        reps_min: data.reps_min,
        reps_max: data.reps_max,
        additional_information: data.additional_information,
      });

      setTrainings(current => [...current, response.data]);

      // Primeiro exercício de um split novo (revelado só localmente
      // pelo "+", sem dado real no backend ainda) -- atualiza a
      // contagem real de splits, senão ele só aparece pros outros
      // lugares que dependem disso (ex: botão de apagar) depois de
      // navegar pra outro dia e voltar.
      if (Number(day) > lastDayWithTrainings) {
        getTrainingSplits();
      }
    },
    [day, getTrainingSplits, lastDayWithTrainings, trainingsService, user],
  );

  const handleRemoveTraining = useCallback(
    async (training_id: string) => {
      try {
        await api.delete(`trainings/${training_id}`);

        setTrainings(trainings.filter(training => training.id !== training_id));
        setFilter(filter.filter(training => training.id !== training_id));

        addToast({
          type: 'success',
          title: 'Exercicio removido.',
        });
      } catch (err) {
        addToast({
          type: 'error',
          title: 'Erro na remoção',
          description:
            'Tente novamente. Caso o problema persista entre em contato com a administração do sistema.',
        });
      }
    },
    [addToast, filter, trainings],
  );

  const handleResetTraining = useCallback(async () => {
    if (
      // eslint-disable-next-line no-alert
      !window.confirm(
        'Remover todos os exercícios deste dia? Essa ação não pode ser desfeita.',
      )
    ) {
      return;
    }

    try {
      await Promise.all(
        trainings.map(training => api.delete(`trainings/${training.id}`)),
      );

      setTrainings([]);
      setFilter([]);

      addToast({
        type: 'success',
        title: 'Treino reiniciado com sucesso.',
      });
    } catch {
      addToast({
        type: 'error',
        title: 'Erro ao reiniciar o treino.',
        description: 'Tente novamente mais tarde.',
      });
    }
  }, [addToast, trainings]);

  const handleNotifyStudent = useCallback(async () => {
    if (!user?.id) return;

    setNotifying(true);

    try {
      await trainingsService.notifyTrainingUpdated({ user_id: user.id });

      addToast({
        type: 'success',
        title: 'Aluno notificado com sucesso.',
      });
    } catch {
      addToast({
        type: 'error',
        title: 'Erro ao notificar o aluno.',
        description: 'Tente novamente mais tarde.',
      });
    } finally {
      setNotifying(false);
    }
  }, [addToast, trainingsService, user]);

  const handleOpenSuggestTraining = useCallback(() => {
    if (!user?.id) return;

    addModal(
      <SuggestTraining
        user_avatar={user?.avatar_url}
        user_id={user.id}
        current_day={Number(day)}
        onApplied={() => {
          getTrainings();
          getTrainingSplits();
        }}
      />,
    );
  }, [addModal, day, getTrainingSplits, getTrainings, user]);

  const handleOpenSelectTemplate = useCallback(() => {
    if (!user?.id) return;

    addModal(
      <SelectTrainingTemplate
        user_avatar={user?.avatar_url}
        user_id={user.id}
        onApplied={() => {
          getTrainings();
          getTrainingSplits();
        }}
      />,
    );
  }, [addModal, getTrainingSplits, getTrainings, user]);

  const handlePrint = useCallback(async () => {
    if (!id || preparingPrint) return;

    if (
      (expiration?.is_expired || expiration?.is_expiring_soon) &&
      // eslint-disable-next-line no-alert
      !window.confirm(
        expiration.is_expired
          ? 'A ficha desse aluno está expirada. Deseja imprimir mesmo assim?'
          : 'A ficha desse aluno está prestes a expirar. Deseja imprimir mesmo assim?',
      )
    ) {
      return;
    }

    setPreparingPrint(true);

    try {
      const splits = await trainingsService.getFullWeekTrainings(id);

      if (splits.length === 0) {
        addToast({
          type: 'info',
          title: 'Nenhum treino cadastrado pra imprimir.',
        });
        return;
      }

      setPrintSplits(splits);
    } catch {
      addToast({
        type: 'error',
        title: 'Erro ao preparar a ficha de treino.',
        description: 'Tente novamente mais tarde.',
      });
    } finally {
      setPreparingPrint(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addToast, id, preparingPrint, expiration]);

  useEffect(() => {
    // Só dispara a impressão depois que a ficha completa (todos os splits)
    // estiver montada no DOM -- disparar no clique direto imprimiria só o
    // conteúdo do dia atualmente aberto.
    if (!printSplits) return undefined;

    window.print();

    const handleAfterPrint = () => setPrintSplits(null);
    window.addEventListener('afterprint', handleAfterPrint);

    return () => window.removeEventListener('afterprint', handleAfterPrint);
  }, [printSplits]);

  useEffect(() => {
    getMuscleGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getTrainings();
    getUserData();
    getExpiration();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modals]);

  useEffect(() => {
    setLoading(true);
    getTrainings();
    getTrainingSplits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [day]);

  const MS_PER_DAY = 24 * 60 * 60 * 1000;

  // Mesmo cálculo do app staff (StudentTrainingEditor) -- mantém a
  // informação de prazo consistente nos dois lugares.
  const expirationDaysLabel = useMemo(() => {
    if (!expiration) return null;

    const expiresAt = new Date(expiration.expires_at);
    const expiresAtLabel = format(expiresAt, 'dd/MM/yyyy');
    const now = new Date();

    if (expiration.is_expired) {
      const daysOverdue = Math.max(
        0,
        Math.floor((now.getTime() - expiresAt.getTime()) / MS_PER_DAY),
      );

      return daysOverdue === 0
        ? `Expirado hoje (${expiresAtLabel})`
        : `Expirado há ${daysOverdue} dia${daysOverdue === 1 ? '' : 's'} (${expiresAtLabel})`;
    }

    const daysRemaining = Math.max(
      0,
      Math.ceil((expiresAt.getTime() - now.getTime()) / MS_PER_DAY),
    );

    return daysRemaining === 0
      ? `Vence hoje (${expiresAtLabel})`
      : `Vence em ${daysRemaining} dia${daysRemaining === 1 ? '' : 's'} (${expiresAtLabel})`;
  }, [expiration]);

  const dominantMuscleGroup = useMemo(() => {
    const counts = new Map<string, number>();

    trainings.forEach(training => {
      const main =
        training.exercise.muscle_groups.find(group => group.is_main) ||
        training.exercise.muscle_groups[0];

      const name = main?.muscle_group?.name || main?.name;

      if (name) {
        counts.set(name, (counts.get(name) || 0) + 1);
      }
    });

    let topName: string | undefined;
    let topCount = 0;

    counts.forEach((count, name) => {
      if (count > topCount) {
        topCount = count;
        topName = name;
      }
    });

    return topName;
  }, [trainings]);

  // Nomes de TODOS os grupos musculares presentes no dia (não só o
  // dominante, que é só pro chip "Foco principal" acima) -- ordenado
  // por quantos exercícios de cada grupo o dia tem.
  const dayMuscleGroupNames = useMemo(() => {
    const counts = new Map<string, number>();

    trainings.forEach(training => {
      const main =
        training.exercise.muscle_groups.find(group => group.is_main) ||
        training.exercise.muscle_groups[0];

      const name = main?.muscle_group?.name || main?.name;

      if (name) {
        counts.set(name, (counts.get(name) || 0) + 1);
      }
    });

    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([name]) => name);
  }, [trainings]);

  const estimatedTime = useMemo(() => {
    const totalSeconds = trainings.reduce((total, training) => {
      if (!training.sets_min && !training.sets_max) return total;

      const sets = training.sets_max || training.sets_min || 0;
      const rest = training.interval_max || training.interval_min || 0;
      const executionSecondsPerSet = 40;

      return total + sets * (rest + executionSecondsPerSet);
    }, 0);

    if (!totalSeconds) return undefined;

    return `~${Math.round(totalSeconds / 60)}min`;
  }, [trainings]);

  const dayLabel = formatSplitLabel(Number(day)).title;

  return (
    <Painel loading={loading}>
      <Container>
        {variant === 'full' && (
          <BackLink type="button" onClick={() => navigate(`/users/${id}`)}>
            <Icon name="arrow_back" size={16} color={colors.primary} />
            Detalhes do aluno
          </BackLink>
        )}

        <SummarySection>
          <SummaryLeft>
            <NameRow>
              <ContainerAvatar>
                <img src={user?.avatar_url || noAvatar} alt="avatar user" />
              </ContainerAvatar>
              <StudentName>{formatName(user?.fullname || '')}</StudentName>
            </NameRow>

            <LastTraining>
              Último treino registrado: {lastTraining}
            </LastTraining>

            {!!expiration?.last_edited_by_name && (
              <LastTraining>
                Última edição por {expiration.last_edited_by_name}
                {expiration.last_edited_at &&
                  `, em ${format(
                    new Date(expiration.last_edited_at),
                    'dd/MM/yyyy',
                  )}`}
              </LastTraining>
            )}
          </SummaryLeft>

          <ChipsRow>
            <Chip>
              <p>Foco principal</p>
              <span>{dominantMuscleGroup || '—'}</span>
            </Chip>
            <Chip>
              <p>Split atual</p>
              <span>{dayLabel}</span>
            </Chip>
          </ChipsRow>
        </SummarySection>

        {!!expiration && (
          <ExpirationBanner
            severity={expiration.is_expired ? 'expired' : 'expiring'}
            warning={expiration.is_expired || expiration.is_expiring_soon}
          >
            <span>
              {expiration.is_expired
                ? 'A ficha desse aluno está expirada.'
                : expiration.is_expiring_soon
                ? 'A ficha desse aluno está prestes a expirar.'
                : 'Ficha válida.'}{' '}
              {expirationDaysLabel}
            </span>

            <ExpirationControls>
              <ExpirationOverrideLabel htmlFor="expiration_override">
                Prazo (dias) pra este aluno
              </ExpirationOverrideLabel>
              <ExpirationOverrideInput
                id="expiration_override"
                type="number"
                min={1}
                placeholder="Padrão"
                value={overrideInput}
                onChange={e => setOverrideInput(e.target.value)}
                onBlur={handleSaveExpirationOverride}
                disabled={savingOverride}
              />
              <SaveOverrideButton
                type="button"
                onClick={handleSaveExpirationOverride}
                disabled={savingOverride}
                title="Confirmar prazo"
              >
                <Icon name="check" size={16} color={colors.onPrimary} />
              </SaveOverrideButton>
            </ExpirationControls>
          </ExpirationBanner>
        )}

        <EditorToolbar>
          <SegmentedTabs>
            <ButtonsSplits
              value="day"
              name="days"
              day={Number(day)}
              userId={id || ''}
              lastTraining={lastDayWithTrainings}
              basePath={basePath}
              onSplitRemoved={handleSplitRemoved}
            />
          </SegmentedTabs>

          <ToolbarActions>
            <GradientButton
              type="button"
              onClick={handleNotifyStudent}
              disabled={notifying}
            >
              {notifying ? 'Notificando...' : 'Notificar ficha atualizada'}
            </GradientButton>
          </ToolbarActions>
        </EditorToolbar>

        <Form ref={formRef} onSubmit={() => undefined}>
          <FiltersRow>
            <SearchField
              placeholder="Buscar exercício no treino"
              value={searchText}
              onChange={setSearchText}
            />
            <SelectWrapper>
              <Select
                name="muscleGroup"
                options={muscleGroups.map(group => ({
                  label: group.name as string,
                  value: group.id as string,
                }))}
                placeholder="Grupo muscular"
                onChange={handleChangeSelectForm}
                blurInputOnSelect
              />
            </SelectWrapper>
          </FiltersRow>
        </Form>

        <Grid>
          <MainColumn>
            <SectionHeader>
              <SectionTitle>
                {dayMuscleGroupNames.length
                  ? dayMuscleGroupNames.join(' + ')
                  : 'Treino do dia'}
              </SectionTitle>
              {!!estimatedTime && (
                <EstimatedTime>Tempo estimado: {estimatedTime}</EstimatedTime>
              )}
            </SectionHeader>

            {filter.map(training => (
              <TrainingCard
                key={training.id}
                id={training.id}
                exerciseName={training.exercise.name}
                muscleGroups={training.exercise.muscle_groups}
                description={training.exercise.description}
                thumbnail_url={
                  getExercisePrimaryMedia(training.exercise.media)
                    ?.thumbnail_url
                }
                sets_min={training.sets_min}
                sets_max={training.sets_max}
                reps_min={training.reps_min}
                reps_max={training.reps_max}
                interval_min={training.interval_min}
                interval_max={training.interval_max}
                additional_information={training.additional_information}
                functionChangeButton={() =>
                  addModal(<ChangeTrainingUser training_id={training.id} />)
                }
                functionRemoveButton={() => handleRemoveTraining(training.id)}
              />
            ))}

            <AddExerciseButton
              type="button"
              onClick={() =>
                addModal(
                  <AddExercisesInTraining
                    user_avatar={user?.avatar_url}
                    training_number={Number(day)}
                    exercises_already_registered_of_day={trainings.map(
                      training => training.exercise,
                    )}
                    onAdd={handleAddExercise}
                  />,
                )
              }
            >
              <Icon name="add_box" size={28} color={colors.onSurfaceVariant} />
              <span className="label">Adicionar exercício</span>
            </AddExerciseButton>
          </MainColumn>

          <Sidebar>
            <VolumeAnalysis exercises={trainings.map(t => t.exercise)} />
            <QuickTools
              hasExercises={!!trainings.length}
              onReset={handleResetTraining}
              onSuggestTraining={handleOpenSuggestTraining}
              hasTrainingSuggestionFeature={hasTrainingSuggestionFeature}
              onSelectTemplate={handleOpenSelectTemplate}
              hasTrainingTemplatesFeature={hasTrainingTemplatesFeature}
              onPrint={handlePrint}
              preparingPrint={preparingPrint}
            />
          </Sidebar>
        </Grid>

        {printSplits && (
          <PrintWorkoutSheet
            gymName={loggedUser?.gym?.name}
            gymLogoUrl={loggedUser?.gym?.logo_url}
            studentName={formatName(user?.fullname || '')}
            splits={printSplits}
            lastEditedByName={expiration?.last_edited_by_name}
          />
        )}
      </Container>
    </Painel>
  );
};

export { UserExercises };
